import { DataPlaneResourcePathPrefix } from '../constants/data-plane';
import { StringifiedPattern } from '../constants/patterns';
import { isValidDataPlaneUri, parseDataPlanePath } from '../ids/data-plane';
import { ClientError } from '../models/common';
import { DataPlaneIdTokenSet } from '../models/data-plane';
import { isUndefinedOrWhiteSpace } from '../utilities/string';
import { tryOrDefault } from '../utilities/try-or-default';

/* eslint-disable security/detect-non-literal-regexp */
// Justification: regex is non-literal, but is not constructed from user input.
const snapshotPathPattern = new RegExp(
    `^/${DataPlaneResourcePathPrefix.Project}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.User}/${StringifiedPattern.Guid}/${DataPlaneResourcePathPrefix.DevBox}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.Snapshot}/${StringifiedPattern.ResourceName}$`,
    'i'
);
/* eslint-enable security/detect-non-literal-regexp */

export const createSnapshotDataPlaneUri = (
    tokens: DataPlaneIdTokenSet<'snapshotId' | 'devBoxName' | 'devCenter' | 'projectName' | 'user'>
): string => {
    const { snapshotId, devBoxName, devCenter, projectName, user } = tokens;

    if (isUndefinedOrWhiteSpace(snapshotId)) {
        throw new ClientError('Snapshot Id cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(devBoxName)) {
        throw new ClientError('Dev box name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(devCenter)) {
        throw new ClientError('Dev center token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(projectName)) {
        throw new ClientError('Project name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(user)) {
        throw new ClientError('User token cannot be undefined or white space');
    }

    const path = `/${[
        DataPlaneResourcePathPrefix.Project,
        projectName.toLowerCase(),
        DataPlaneResourcePathPrefix.User,
        user.toLowerCase(),
        DataPlaneResourcePathPrefix.DevBox,
        devBoxName.toLowerCase(),
        DataPlaneResourcePathPrefix.Snapshot,
        snapshotId,
    ].join('/')}`;

    const uri = new URL(path, devCenter);

    return `${uri}`;
};

export const getTokensFromSnapshotDataPlaneUri = (
    uri: string
): DataPlaneIdTokenSet<'snapshotId' | 'devBoxName' | 'devCenter' | 'projectName' | 'user'> => {
    if (!isValidSnapshotDataPlaneUri(uri)) {
        throw new ClientError('Given URI was not a valid Data Plane URI for a dev box snapshot');
    }

    const { origin, pathname } = new URL(uri);
    const [, projectName, , user, , devBoxName, , snapshotId] = parseDataPlanePath(pathname);

    return {
        snapshotId,
        devBoxName,
        devCenter: origin,
        projectName,
        user,
    };
};

export const isValidSnapshotDataPlaneUri = (uri: string): boolean => isValidDataPlaneUri(uri, snapshotPathPattern);

export const tryCreateSnapshotDataPlaneUri = tryOrDefault(createSnapshotDataPlaneUri);
export const tryGetTokensFromSnapshotDataPlaneUri = tryOrDefault(getTokensFromSnapshotDataPlaneUri);
